import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useToast } from '../../components/Toast/ToastContext';
import {
  rescueChipStatsQuery,
  RescueChipStatsData,
  rescueDeviceStatsQuery,
  RescueDeviceStatsData,
  rescueGwaDashboardStatsQuery,
  RescueGwaDashboardStatsData,
  rescueDevicesProgramQuery,
  RescueDevicesProgramData,
  rescuePetsQuery,
  RescuePetsData,
  RescuePetGql,
  rescueViewingChipsQuery,
  RescueViewingChipsData,
  type RescueDeviceItemPetGql,
  removePetMutation,
  rescueCreatePetFosterTransferMutation,
  rescueRecallPetFosterTransferMutation,
  unassignDeviceMutation,
} from '../../lib/rescueQueries';
import { ChipStatusType } from '@/types/gql-op-types';
import { rescueCurrent501c3DocumentQuery, type RescueCurrent501c3DocumentData } from '../../lib/documentQueries';
import {
  recallChipMutation,
  reassignChipToResellerMutation,
} from '@/appview/ResellerChipDetails/ResellerChipDetails.graphql';
import { assignNewOwnerToChipMutation } from '@/appview/RegisterChipV2/RegisterChip.graphql';
import SearchBar from '../../components/SearchBar/SearchBar';
import Button from '../../components/Button/Button';
import useAllDeviceItems from '../../hooks/useAllDeviceItems';
import PetsTable, { type PetRow } from './PetsTable';
import RemovePetModal from './RemovePetModal';
import AssignDeviceModal from './AssignDeviceModal';
import AssignDeviceToPetModal from './AssignDeviceToPetModal';
import AddChipToPetModal from './AddChipToPetModal';
import ViewPetModal from './ViewPetModal';
import PetFormModal from './PetFormModal';
import TransferToAdopterModal from '../../components/TransferToAdopterModal/TransferToAdopterModal';
import TransferToFosterModal from './TransferToFosterModal';
import TransferChipModal, {
  DeviceAppliedChipFailedError,
  type AdopterFormData,
  type DeviceTransferAction,
} from '../Chips/TransferChipModal';
import { Chip } from '../../lib/types';
import { mapAdopterFormToContactInfoInput } from '../../lib/chipAdapters';
import { useTransferPetWithChipToAdopter } from '../../lib/useTransferPetWithChipToAdopter';
import {
  trackDashboardViewed,
  trackActionItemClicked,
  trackPetViewed,
  trackPetRemoved,
  trackDeviceTransferToFoster,
  trackDeviceTransferToAdopt,
  trackDeviceRecalled,
  trackChipOwnerAdded,
} from '../../analytics';
import styles from './Dashboard.module.scss';

type Urgency = 'warning' | 'error' | 'info';

const URGENCY_COLORS: Record<Urgency, { bg: string; text: string }> = {
  error: { bg: '#fbefee', text: '#e0352a' },
  warning: { bg: '#fffcbd', text: '#b8860b' },
  info: { bg: '#e1f0ff', text: '#1360f4' },
};

function getT2aSubtext(pending: number, completed: number) {
  const total = completed + pending;
  if (total === 0) return { text: 'No transfers yet', color: '' };
  const rate = completed / total;
  const pct = Math.round(rate * 100);
  let color: string;
  if (rate >= 0.8) color = 'var(--success-text, #1B7D3A)';
  else if (rate >= 0.6) color = 'var(--warning-text, #B8860B)';
  else color = 'var(--error-text, #E0352A)';
  return {
    text: `T2A rate: ${pct}% (${completed} completed, ${pending} pending)`,
    color,
  };
}

interface ActionItem {
  title: string;
  description: string;
  link: string;
  urgency: Urgency;
}

/**
 * Tri-state content for the GWA Redemptions card on the Dashboard.
 * Loading shows a neutral placeholder so we don't flash the "Not enrolled" CTA
 * before the query resolves.
 */
function getGwaCardContent(loading: boolean, data: RescueGwaDashboardStatsData | undefined) {
  if (loading && !data) {
    return { value: '\u2014' as string | number, subtext: null as string | null };
  }
  const program = data?.rescueGwaProgram ?? null;
  if (!program) {
    return { value: '\u2014' as string | number, subtext: 'Set up your program \u203A' };
  }
  return {
    value: program.monthRedemptions,
    subtext: `last 30 days \u00B7 ${program.totalRedemptions} total`,
  };
}

/**
 * Tri-state content for the GPS Devices card. Mirrors the GWA card: loading
 * shows a neutral placeholder, unenrolled prompts the user to set up, enrolled
 * shows the active device count + T2A subtext.
 */
function getDevicesCardContent(
  loading: boolean,
  data: RescueDevicesProgramData | undefined,
  activeDevices: number,
  t2a: { text: string; color: string | null },
) {
  if (loading && !data) {
    return { value: '\u2014' as string | number, subtext: null as string | null, subtextColor: null as string | null };
  }
  if (!data?.rescueDevicesProgram?.enrolled) {
    return {
      value: '\u2014' as string | number,
      subtext: 'Set up your program \u203A',
      subtextColor: null as string | null,
    };
  }
  return { value: activeDevices, subtext: t2a.text, subtextColor: t2a.color };
}

function buildActionItems(pending: number, completed: number, needs501c3Upload: boolean): ActionItem[] {
  const items: ActionItem[] = [];
  if (needs501c3Upload) {
    items.push({
      title: 'Upload your 501c3 determination letter',
      description: 'Verifies your nonprofit status with the Fi team — required to keep tax-exempt ordering',
      link: '/rescue/account',
      urgency: 'warning',
    });
  }
  const total = completed + pending;
  if (total > 0 && pending > 0) {
    const rate = completed / total;
    const pct = Math.round(rate * 100);
    const urgency: Urgency = rate < 0.6 ? 'error' : rate < 0.8 ? 'warning' : 'info';
    items.push({
      title: `T2A acceptance rate is ${pct}%`,
      description: `${pending} adopter${
        pending === 1 ? ' hasn\u2019t' : 's haven\u2019t'
      } completed setup \u2014 consider resending setup emails`,
      link: '/rescue/devices',
      urgency,
    });
  }
  return items;
}

function useModal<T = void>() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);
  return {
    open,
    data,
    show(value?: T) {
      setData(value ?? null);
      setOpen(true);
    },
    close() {
      setOpen(false);
      setData(null);
    },
  };
}

const ALL_DEVICES_PAGE_SIZE = 100;
const MAX_FETCH_MORE_RETRIES = 3;

type RegisteredChipItem = RescueViewingChipsData['currentUser']['chipReseller'] extends infer R
  ? R extends { chips: { chips: Array<infer C> } }
    ? C
    : never
  : never;

/**
 * Fetches all pages of the rescue's ACTIVE (registered) chips from the same
 * source the Chips page "Registered" tab uses. We need this on the Dashboard
 * to surface chip info for pending-adoption and adopted pets, whose `pet.chip`
 * field is gated on the rescue user's household membership in the (new)
 * adopter household — once that ends, `pet.chip` resolves to null even though
 * the rescue still owns the chip via the reseller relationship.
 *
 * Pass `skip=true` to avoid the request entirely (e.g. when there are no
 * pending/adopted pets needing a fallback chip).
 */
function useAllRegisteredChips(skip: boolean) {
  const { data, loading, fetchMore } = useQuery<RescueViewingChipsData>(rescueViewingChipsQuery, {
    variables: { statusType: ChipStatusType.ACTIVE, limit: ALL_DEVICES_PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-first',
    skip,
  });

  const [extraItems, setExtraItems] = useState<RegisteredChipItem[]>([]);
  const paginating = useRef(false);
  const retries = useRef(0);
  const firstPageItems = data?.currentUser?.chipReseller?.chips?.chips as RegisteredChipItem[] | undefined;
  const total = data?.currentUser?.chipReseller?.chips?.metadata?.totalChipCount ?? 0;
  // Memoize so the combined array has a stable reference across renders when
  // neither the first page nor the accumulated extras changed — otherwise
  // downstream memos (e.g. `registeredChipByPetId`) recompute every render.
  const items = useMemo<RegisteredChipItem[]>(() => {
    const first = firstPageItems ?? [];
    return extraItems.length > 0 ? [...first, ...extraItems] : first;
  }, [firstPageItems, extraItems]);
  const hasMore = items.length < total;

  useEffect(() => {
    setExtraItems([]);
    retries.current = 0;
  }, [data]);

  useEffect(() => {
    if (!hasMore || loading || paginating.current || retries.current >= MAX_FETCH_MORE_RETRIES) {
      return;
    }
    let cancelled = false;
    paginating.current = true;
    fetchMore({
      variables: { statusType: ChipStatusType.ACTIVE, limit: ALL_DEVICES_PAGE_SIZE, offset: items.length },
    })
      .then(({ data: result }) => {
        const next = (result?.currentUser?.chipReseller?.chips?.chips ?? []) as RegisteredChipItem[];
        if (!cancelled && next.length) {
          setExtraItems((prev) => [...prev, ...next]);
          retries.current = 0;
        }
      })
      .catch(() => {
        if (!cancelled) {
          retries.current += 1;
        }
      })
      .finally(() => {
        paginating.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [hasMore, loading, items.length, fetchMore]);

  return { items };
}

export default function Dashboard() {
  const history = useHistory();
  const { showToast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  // Chip counts — use the dedicated stats resolver which runs efficient
  // parallel COUNT queries instead of the paginated chips query whose
  // getManyAndCount() can timeout for large rescues.
  const { data: chipStatsData } = useQuery<RescueChipStatsData>(rescueChipStatsQuery, {
    fetchPolicy: 'cache-and-network',
  });

  const chipStock = chipStatsData?.currentUser?.chipReseller?.stats?.totalStock ?? 0;
  const chipRegistered = chipStatsData?.currentUser?.chipReseller?.stats?.totalRegistered ?? 0;

  // Device / T2A stats
  const { data: deviceStatsData } = useQuery<RescueDeviceStatsData>(rescueDeviceStatsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const deviceStats = deviceStatsData?.rescueDeviceStats;
  const activeDevices = deviceStats?.totalActiveDevices ?? 0;
  const pendingCount = deviceStats?.pendingAdoptionsCount ?? 0;
  const completedCount = deviceStats?.completedAdoptionsCount ?? 0;
  const t2a = getT2aSubtext(pendingCount, completedCount);
  // Nudge the 501c3 upload only when there's nothing awaiting or passing review —
  // no document at all, or one the Fi team rejected. A PENDING or APPROVED letter
  // needs no action from the rescue. Deliberately not gated on `taxExemptType`;
  // see the matching note on the Account page's Card501c3.
  //
  // `doc501c3Loading` has to be part of the condition, not just the data: while
  // the query is in flight there's no document yet, which is indistinguishable
  // from "never uploaded one" — without it, a rescue whose letter is already
  // approved sees the nudge flash in on every cold load.
  const { data: doc501c3Data, loading: doc501c3Loading } = useQuery<RescueCurrent501c3DocumentData>(
    rescueCurrent501c3DocumentQuery,
  );
  const current501c3 = doc501c3Data?.rescueCurrent501c3Document;
  const needs501c3Upload = !doc501c3Loading && (!current501c3 || current501c3.status === 'REJECTED');
  const actionItems = buildActionItems(pendingCount, completedCount, needs501c3Upload);

  // GWA enrollment + redemption counts
  const { data: gwaStatsData, loading: gwaStatsLoading } = useQuery<RescueGwaDashboardStatsData>(
    rescueGwaDashboardStatsQuery,
    { fetchPolicy: 'cache-and-network' },
  );
  const gwaCard = getGwaCardContent(gwaStatsLoading, gwaStatsData);

  // Devices program enrollment — drives the GPS Devices card's CTA state.
  const { data: devicesEnrollmentData, loading: devicesEnrollmentLoading } = useQuery<RescueDevicesProgramData>(
    rescueDevicesProgramQuery,
    { fetchPolicy: 'cache-and-network' },
  );
  const devicesCard = getDevicesCardContent(devicesEnrollmentLoading, devicesEnrollmentData, activeDevices, t2a);

  // ── Unassigned pets ──────────────────────────

  const { data: petsData, loading: petsLoading } = useQuery<RescuePetsData>(rescuePetsQuery);

  // Fetch all device lists per category to derive pet status.
  // Uses cache-first + pagination so we don't miss devices beyond page 1.
  const foster = useAllDeviceItems('IN_FOSTER');
  const pendingDevices = useAllDeviceItems('PENDING_ADOPTIONS');
  const completedDevices = useAllDeviceItems('COMPLETED_ADOPTIONS');
  // Only fetch the rescue's registered chips when we actually have pets whose
  // pet.chip is gated by household membership — otherwise it's an expensive
  // paginated fetch with no consumer on the page.
  //
  // While the device lists are still loading we keep the chips query firing
  // speculatively, otherwise pending/adopted rows on a cold load would render
  // without their fallback chip and re-render once the chips query catches up.
  const skipRegisteredChips =
    !pendingDevices.loading &&
    !completedDevices.loading &&
    pendingDevices.items.length === 0 &&
    completedDevices.items.length === 0;
  const registeredChips = useAllRegisteredChips(skipRegisteredChips);

  // Pet id → chip (from the rescue's ACTIVE chips, same source as the Chips
  // page Registered tab). Used as a fallback for pending-adoption pets whose
  // pet.chip resolver is gated on household membership.
  //
  // `latestContactHistoryEntry` is threaded through so the row's chip dropdown
  // can derive `hasOwner` correctly — without it, every fallback chip would
  // appear ownerless and the "Add Owner" action would re-surface for chips
  // that already have a contact on file.
  type FallbackChip = {
    id: string;
    shortId: string;
    serialNumber: string;
    latestContactHistoryEntry: { contactInfo?: { primaryEmail?: string | null } | null } | null;
  };
  const registeredChipByPetId = useMemo(() => {
    const map = new Map<string, FallbackChip>();
    for (const chip of registeredChips.items) {
      const petId = chip.pet?.id;
      if (petId) {
        map.set(petId, {
          id: chip.id,
          shortId: chip.shortId,
          serialNumber: chip.serialNumber,
          latestContactHistoryEntry: chip.latestContactHistoryEntry ?? null,
        });
      }
    }
    return map;
  }, [registeredChips.items]);

  type PendingAdoptionAdopter = {
    fullName: string | null;
    email: string | null;
    phone: string | null;
  };

  type DeviceStatusEntry = {
    status: 'foster' | 'pending_adoption' | 'adopted';
    updatedAt?: string | null;
    pet: RescueDeviceItemPetGql;
    pendingAdoption?: PendingAdoptionAdopter | null;
  };

  const deviceStatusEntries = useMemo(() => {
    const entries: DeviceStatusEntry[] = [];
    for (const item of foster.items) {
      entries.push({
        status: 'foster',
        updatedAt: item.transfer?.updatedAt ?? item.transfer?.createdAt ?? null,
        pet: item.pet,
      });
    }
    for (const item of pendingDevices.items) {
      entries.push({
        status: 'pending_adoption',
        updatedAt: item.transfer?.updatedAt ?? item.transfer?.createdAt ?? null,
        pet: item.pet,
        pendingAdoption: item.transfer
          ? {
              fullName: item.transfer.toFullName ?? null,
              email: item.transfer.toEmail ?? null,
              phone: item.transfer.toPhone ?? null,
            }
          : null,
      });
    }
    for (const item of completedDevices.items) {
      entries.push({
        status: 'adopted',
        updatedAt: item.transfer?.completedAt ?? item.transfer?.updatedAt ?? null,
        pet: item.pet,
      });
    }
    return entries;
  }, [foster.items, pendingDevices.items, completedDevices.items]);

  const allPets = useMemo(() => {
    const rescueHousehold = petsData?.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
    const householdPets = rescueHousehold?.household.pets ?? [];

    // Build a map of pet ID → status from device queries
    const statusMap = new Map<string, DeviceStatusEntry>();
    for (const entry of deviceStatusEntries) {
      statusMap.set(entry.pet.id, entry);
    }

    // Start with household pets, applying status overrides
    const seenIds = new Set<string>();
    const result = householdPets.map((pet) => {
      seenIds.add(pet.id);
      const statusEntry = statusMap.get(pet.id);
      return {
        ...pet,
        hasDevice: !!pet.device,
        hasChip: !!pet.chip,
        status: statusEntry?.status ?? ('rescue' as const),
        updatedAt: statusEntry?.updatedAt ?? pet.updatedAt ?? null,
        pendingAdoption: statusEntry?.pendingAdoption ?? null,
      };
    });

    // Add foster/pending/adopted pets not in the rescue household
    for (const entry of deviceStatusEntries) {
      if (!seenIds.has(entry.pet.id)) {
        seenIds.add(entry.pet.id);
        const p = entry.pet;
        // For pending-adoption and adopted pets, pet.chip resolves to null
        // once household membership ends. Match against the reseller's
        // registered chips by pet id to surface the chip the rescue still
        // owns.
        const needsChipFallback = entry.status === 'pending_adoption' || entry.status === 'adopted';
        const fallbackChip = needsChipFallback && !p.chip ? (registeredChipByPetId.get(p.id) ?? null) : null;
        const chip = p.chip ?? fallbackChip;
        result.push({
          id: p.id,
          name: p.name,
          gender: p.gender ?? null,
          species: p.species ?? null,
          weight: p.weight ?? null,
          color: p.color ?? null,
          additionalInfo: p.additionalInfo ?? null,
          yearOfBirth: p.yearOfBirth ?? null,
          monthOfBirth: p.monthOfBirth ?? null,
          dayOfBirth: p.dayOfBirth ?? null,
          breed: p.breed ?? null,
          // Carry the photo through so foster/pending/adopted rows show the same
          // thumbnail the device query already fetches (the rescue-household loop
          // above keeps it via the `...pet` spread).
          photos: p.photos ?? null,
          chip,
          device: p.device ? { id: p.device.id, moduleId: p.device.moduleId } : null,
          updatedAt: entry.updatedAt ?? null,
          hasDevice: !!p.device,
          hasChip: !!chip,
          status: entry.status,
          pendingAdoption: entry.pendingAdoption ?? null,
        });
      }
    }

    return result;
  }, [petsData, deviceStatusEntries, registeredChipByPetId]);

  const filteredPets = useMemo(() => {
    let result = allPets;
    if (statusFilter === 'rescue') {
      result = result.filter((pet) => pet.status === 'rescue');
    } else if (statusFilter === 'foster') {
      result = result.filter((pet) => pet.status === 'foster');
    } else if (statusFilter === 'adopted') {
      result = result.filter((pet) => pet.status === 'pending_adoption' || pet.status === 'adopted');
    } else if (statusFilter === 'no_device') {
      result = result.filter((pet) => !pet.hasDevice);
    }
    if (searchQuery) {
      const lower = searchQuery.toLowerCase();
      result = result.filter(
        (pet) => pet.name.toLowerCase().includes(lower) || (pet.breed?.name ?? '').toLowerCase().includes(lower),
      );
    }
    return result;
  }, [allPets, searchQuery, statusFilter]);

  const mutationOpts = { refetchQueries: 'active' as const };
  const [removePetMut] = useMutation(removePetMutation, mutationOpts);
  const [createFosterTransferMut] = useMutation(rescueCreatePetFosterTransferMutation, mutationOpts);
  const [recallFosterTransferMut] = useMutation(rescueRecallPetFosterTransferMutation, mutationOpts);
  const [unassignDeviceMut] = useMutation(unassignDeviceMutation, mutationOpts);
  const [recallChipMut] = useMutation(recallChipMutation, mutationOpts);
  const [reassignToReseller] = useMutation(reassignChipToResellerMutation, mutationOpts);
  const [assignNewOwner] = useMutation(assignNewOwnerToChipMutation, mutationOpts);
  const transferPetWithChip = useTransferPetWithChipToAdopter(mutationOpts);

  const removePetModal = useModal<{
    id: string;
    name: string;
    species?: string | null;
    breed?: { name: string } | null;
    chip?: { id: string } | null;
    device?: { id: string } | null;
  }>();
  const fosterModal = useModal<{ moduleId: string; petId: string }>();
  const assignDeviceModal = useModal<string>();
  const viewPetModal = useModal<string>();
  const createPetModal = useModal();
  const editPetModal = useModal<string>();
  const addChipModal = useModal<{
    id: string;
    name: string;
    breed?: { name: string } | null;
    pendingAdoption?: PendingAdoptionAdopter | null;
  }>();
  const viewPetTarget = viewPetModal.data ? (allPets.find((p) => p.id === viewPetModal.data) ?? null) : null;
  const editPetTarget = editPetModal.data ? (allPets.find((p) => p.id === editPetModal.data) ?? null) : null;
  const assignToPetModal = useModal<RescuePetGql>();
  const adopterModal = useModal<{
    moduleId: string;
    petId: string;
    petName: string | null;
    // The chip's GraphQL `id` field — what assignNewOwnerToChip expects as
    // `chipId`. (Backend resolver returns shortId, so this equals shortId.)
    chipId: string | null;
    chipSerialNumber: string | null;
  }>();
  const addOwnerModal = useModal<Chip>();

  // Memoize the device hand-off prop for TransferChipModal so the modal sees a
  // stable object reference until the underlying chip/pet pairing changes.
  const addOwnerDevice = useMemo(() => {
    const chip = addOwnerModal.data;
    if (!chip) return null;
    const pet = allPets.find((p) => p.chip?.id === chip._graphqlId);
    return pet?.device ? { moduleId: pet.device.moduleId } : null;
  }, [addOwnerModal.data, allPets]);

  function handleOpenRemovePet(petId: string, petName: string) {
    const pet = allPets.find((p) => p.id === petId);
    removePetModal.show({
      id: petId,
      name: petName,
      species: pet?.species ?? null,
      breed: pet?.breed ?? null,
      chip: pet?.chip ? { id: pet.chip.id } : null,
      device: pet?.device ? { id: pet.device.id } : null,
    });
  }

  async function handleConfirmRemovePet(petId: string, petName: string) {
    const pet = allPets.find((p) => p.id === petId);
    const hadChip = !!pet?.chip;
    const hadDevice = !!pet?.device;
    try {
      await removePetMut({ variables: { subjectId: petId } });
      trackPetRemoved(hadChip, hadDevice);
      showToast(`${petName} removed`);
    } catch (err) {
      showToast(`Failed to remove pet: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleViewPet(pet: PetRow) {
    trackPetViewed(!!pet.chip, !!pet.device, pet.status ?? 'rescue', pet.species ?? undefined);
    viewPetModal.show(pet.id);
  }

  function handleEditPet(petId: string) {
    editPetModal.show(petId);
  }

  function handleOpenAddChip(petId: string) {
    const pet = allPets.find((p) => p.id === petId) ?? viewPetTarget;
    if (pet) {
      addChipModal.show(pet);
    }
  }

  function handleOpenAssignDevice(petId: string) {
    const pet = allPets.find((p) => p.id === petId);
    if (pet) {
      assignToPetModal.show(pet);
    } else {
      assignDeviceModal.show(petId);
    }
  }

  async function handleTransferToAdopter(firstName: string, lastName: string, email: string, phone: string) {
    // Capture modal data before the modal closes and clears its data.
    const petId = adopterModal.data?.petId;
    const moduleId = adopterModal.data?.moduleId ?? '';
    const chipId = adopterModal.data?.chipId ?? null;
    if (!petId) {
      return;
    }

    let result;
    try {
      result = await transferPetWithChip({
        petId,
        chipId,
        adopter: {
          firstName,
          lastName,
          email,
          phone,
          street: '',
          apt: '',
          city: '',
          state: '',
          zip: '',
        },
      });
      trackDeviceTransferToAdopt('dashboard_pet_row');
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
      return;
    }

    if (result.chipAssigned) {
      trackChipOwnerAdded('dashboard_t2a');
      showToast(`Transfer initiated for ${moduleId}; chip ownership updated`);
    } else if (result.chipError) {
      const detail = result.chipError instanceof Error ? result.chipError.message : 'Unknown error';
      showToast(`Transfer initiated for ${moduleId}, but chip transfer failed: ${detail}`);
    } else {
      showToast(`Transfer initiated for ${moduleId}`);
    }
  }

  async function handleTransferToFoster(
    petId: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
  ) {
    try {
      await createFosterTransferMut({
        variables: {
          input: {
            petId,
            email,
            firstName,
            lastName: lastName || undefined,
            phoneNumber: phone || undefined,
          },
        },
      });
      trackDeviceTransferToFoster('dashboard_pet_row');
      showToast('Assigned to foster');
    } catch (err) {
      showToast(`Failed to transfer: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleRecallDevice(moduleId: string, petId: string) {
    try {
      await recallFosterTransferMut({ variables: { input: { petId } } });
      trackDeviceRecalled('dashboard_pet_row');
      showToast(`${moduleId} recalled to rescue`);
    } catch (err) {
      showToast(`Failed to recall: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  async function handleUnassignDevice(moduleId: string, petId: string) {
    try {
      await unassignDeviceMut({ variables: { moduleId, petId } });
      showToast(`Pet removed from ${moduleId}`);
    } catch (err) {
      showToast(`Failed to remove: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleDeviceAction(actionKey: string, moduleId: string, petId: string) {
    if (actionKey === 'view') {
      history.push(`/rescue/devices?search=${encodeURIComponent(moduleId)}`);
    } else if (actionKey === 'transferAdopter') {
      const pet = allPets.find((p) => p.id === petId);
      adopterModal.show({
        moduleId,
        petId,
        petName: pet?.name ?? null,
        chipId: pet?.chip?.id ?? null,
        chipSerialNumber: pet?.chip?.serialNumber ?? null,
      });
    } else if (actionKey === 'transferFoster') {
      fosterModal.show({ moduleId, petId });
    } else if (actionKey === 'recallDevice') {
      handleRecallDevice(moduleId, petId);
    } else if (actionKey === 'removeFromPet') {
      handleUnassignDevice(moduleId, petId);
    }
  }

  async function handleChipAction(
    actionKey: string,
    chipId: string,
    shortId: string,
    serialNumber: string,
    petId: string,
  ) {
    if (actionKey === 'viewChip') {
      history.push(`/rescue/chips?search=${encodeURIComponent(shortId)}`);
    } else if (actionKey === 'addOwner') {
      const pet = allPets.find((p) => p.id === petId);
      if (!pet?.chip) return;
      // Construct the minimal Chip view-model that TransferChipModal renders
      // (id, petName, species, breed) plus _graphqlId for the submit mutation.
      addOwnerModal.show({
        _graphqlId: pet.chip.id,
        id: pet.chip.shortId,
        fullChipId: pet.chip.serialNumber,
        petName: pet.name,
        species: pet.species ?? null,
        breed: pet.breed?.name ?? null,
      } as Chip);
    } else if (actionKey === 'clearOwner') {
      try {
        // Reassign to reseller to get the ResellerChip ID, then recall to clear owner
        const { data } = await reassignToReseller({
          variables: { input: { serialNumber, clearOwnerInfo: false } },
        });
        const resellerChipId = data?.reassignChipToReseller?.id;
        if (resellerChipId) {
          await recallChipMut({ variables: { input: { chipId: resellerChipId } } });
        }
        showToast('Owner cleared');
      } catch (err) {
        showToast(`Failed to clear owner: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  }

  async function handleAddOwnerSubmit(_chipId: string, adopter: AdopterFormData, deviceAction?: DeviceTransferAction) {
    // TransferChipModal passes its display id back; the mutation needs the
    // GraphQL chip id, which we stashed on the modal data when opening.
    const chip = addOwnerModal.data;
    if (!chip?._graphqlId) return;
    const pet = allPets.find((p) => p.chip?.id === chip._graphqlId);

    if (deviceAction === 'transfer' && pet?.device) {
      const moduleId = pet.device.moduleId;
      let result;
      try {
        result = await transferPetWithChip({
          petId: pet.id,
          chipId: chip._graphqlId,
          adopter,
        });
      } catch (err) {
        const detail = err instanceof Error ? err.message : 'Unknown error';
        showToast(`Failed to add owner: ${detail}`);
        throw err;
      }
      trackDeviceTransferToAdopt('dashboard_add_owner');
      showToast(`Transfer initiated for ${moduleId}`);
      if (result.chipError) {
        throw DeviceAppliedChipFailedError.fromDeviceAction(
          `${moduleId} adoption transfer was initiated`,
          result.chipError instanceof Error ? result.chipError.message : 'Unknown error',
        );
      }
      trackChipOwnerAdded('dashboard_pet_row');
      return;
    }

    let deviceApplied: { description: string } | null = null;
    try {
      if (deviceAction === 'return' && pet?.device) {
        const moduleId = pet.device.moduleId;
        await unassignDeviceMut({ variables: { moduleId, petId: pet.id } });
        showToast(`${moduleId} returned to rescue stock`);
        deviceApplied = { description: `${moduleId} was returned to rescue stock` };
      }

      await assignNewOwner({
        variables: {
          input: {
            chipId: chip._graphqlId,
            contactInfo: mapAdopterFormToContactInfoInput(adopter),
          },
        },
      });
      trackChipOwnerAdded('dashboard_pet_row');
    } catch (err) {
      const detail = err instanceof Error ? err.message : 'Unknown error';
      if (deviceApplied) {
        throw DeviceAppliedChipFailedError.fromDeviceAction(deviceApplied.description, detail);
      }
      showToast(`Failed to add owner: ${detail}`);
      throw err;
    }
  }

  // ── Analytics ──────────────────────────

  const tracked = useRef(false);
  useEffect(() => {
    // Also wait on the 501c3 query: it feeds an action item, so firing before it
    // settles would report a count that's short by one.
    if (!tracked.current && (chipStatsData || deviceStatsData) && !doc501c3Loading) {
      tracked.current = true;
      const total = pendingCount + completedCount;
      const rate = total > 0 ? Math.round((completedCount / total) * 100) : 0;
      trackDashboardViewed(actionItems.length, rate);
    }
  }, [chipStatsData, deviceStatsData, doc501c3Loading]); // eslint-disable-line react-hooks/exhaustive-deps -- tracked.current guard ensures single fire

  return (
    <div>
      <h1>Dashboard</h1>

      <div className={styles.summaryCards}>
        <Link to="/rescue/chips" className={styles.summaryCard}>
          <div className={styles.cardLabel}>Fi Nano Microchips</div>
          <div className={styles.cardValue}>{chipStock + chipRegistered}</div>
          <div className={styles.cardSubtext}>
            {chipStock} in stock, {chipRegistered} registered
          </div>
        </Link>

        <Link to="/rescue/devices" className={styles.summaryCard}>
          <div className={styles.cardLabel}>GPS Devices</div>
          <div className={styles.cardValue}>{devicesCard.value}</div>
          {devicesCard.subtext && (
            <div
              className={styles.cardSubtext}
              style={devicesCard.subtextColor ? { color: devicesCard.subtextColor, fontWeight: 600 } : undefined}
            >
              {devicesCard.subtext}
            </div>
          )}
        </Link>

        <Link to="/rescue/gwa" className={styles.summaryCard}>
          <div className={styles.cardLabel}>GWA Redemptions</div>
          <div className={styles.cardValue}>{gwaCard.value}</div>
          {gwaCard.subtext && <div className={styles.cardSubtext}>{gwaCard.subtext}</div>}
        </Link>
      </div>

      {actionItems.length > 0 && (
        <div className={styles.contentCard}>
          <div className={styles.contentCardHeader}>
            <h2 className={styles.contentCardTitle}>Action Items</h2>
          </div>
          <div className={styles.actionItems}>
            {actionItems.map((item) => {
              const colors = URGENCY_COLORS[item.urgency];
              return (
                <Link
                  key={item.title}
                  to={item.link}
                  className={styles.actionItem}
                  onClick={() => trackActionItemClicked(item.title, item.urgency)}
                >
                  <div className={styles.actionIcon} style={{ background: colors.bg, color: colors.text }}>
                    {item.urgency === 'error' ? '!' : item.urgency === 'warning' ? '!' : 'i'}
                  </div>
                  <div className={styles.actionText}>
                    <div className={styles.actionTitle}>{item.title}</div>
                    <div className={styles.actionMeta}>{item.description}</div>
                  </div>
                  <div className={styles.actionChevron}>&rsaquo;</div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.quickActionsSection}>
        <div className={styles.quickActionsLabel}>Quick actions</div>
        <div className={styles.quickActions}>
          <Link to="/rescue/chips" className={styles.quickActionBtnBlue}>
            Register Chips
          </Link>
          <button type="button" className={styles.quickActionBtnGreen} onClick={() => assignDeviceModal.show('')}>
            Assign Device
          </button>
          <button type="button" className={styles.quickActionBtnGreen} onClick={() => createPetModal.show()}>
            Create a New Pet
          </button>
          <Link to="/rescue/devices" className={styles.quickActionBtnYellow}>
            View Devices
          </Link>
          <Link to="/rescue/help" className={styles.quickActionBtnYellow}>
            Get Help
          </Link>
        </div>
      </div>

      <div className={styles.searchRow}>
        <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search by pet name or breed..." />
        <div className={styles.filterButtons}>
          <Button variant={statusFilter === null ? 'green' : 'ghost'} size="sm" onClick={() => setStatusFilter(null)}>
            All
          </Button>
          <Button
            variant={statusFilter === 'rescue' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('rescue')}
          >
            Rescue
          </Button>
          <Button
            variant={statusFilter === 'foster' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('foster')}
          >
            Foster
          </Button>
          <Button
            variant={statusFilter === 'adopted' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('adopted')}
          >
            Adopted
          </Button>
          <Button
            variant={statusFilter === 'no_device' ? 'green' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('no_device')}
          >
            No Device
          </Button>
        </div>
      </div>

      <PetsTable
        pets={filteredPets}
        loading={petsLoading}
        onAssignDevice={handleOpenAssignDevice}
        onViewPet={handleViewPet}
        onCreatePet={() => createPetModal.show()}
        onDeviceAction={handleDeviceAction}
        onChipAction={handleChipAction}
        onAddChip={(pet) => handleOpenAddChip(pet.id)}
      />

      <RemovePetModal
        open={removePetModal.open}
        onClose={removePetModal.close}
        pet={removePetModal.data}
        onConfirm={handleConfirmRemovePet}
      />

      <AssignDeviceModal
        key={assignDeviceModal.data ?? ''}
        open={assignDeviceModal.open}
        onClose={assignDeviceModal.close}
        initialPetId={assignDeviceModal.data ?? ''}
      />

      <AssignDeviceToPetModal
        open={assignToPetModal.open}
        onClose={assignToPetModal.close}
        pet={assignToPetModal.data}
      />

      <AddChipToPetModal open={addChipModal.open} onClose={addChipModal.close} pet={addChipModal.data} />

      <ViewPetModal
        open={viewPetModal.open}
        onClose={viewPetModal.close}
        pet={viewPetTarget}
        onAssignDevice={handleOpenAssignDevice}
        onAddChip={handleOpenAddChip}
        onRemovePet={handleOpenRemovePet}
        onEditPet={handleEditPet}
      />

      <PetFormModal mode="create" open={createPetModal.open} onClose={createPetModal.close} />

      <PetFormModal mode="edit" open={editPetModal.open} onClose={editPetModal.close} pet={editPetTarget} />

      <TransferToAdopterModal
        open={adopterModal.open}
        onClose={adopterModal.close}
        deviceId={adopterModal.data?.moduleId ?? ''}
        petName={adopterModal.data?.petName ?? null}
        chipSerialNumber={adopterModal.data?.chipSerialNumber ?? null}
        showRescueDiscount
        onTransfer={handleTransferToAdopter}
      />

      <TransferToFosterModal
        open={fosterModal.open}
        onClose={fosterModal.close}
        moduleId={fosterModal.data?.moduleId ?? ''}
        petId={fosterModal.data?.petId ?? ''}
        onTransfer={handleTransferToFoster}
      />

      <TransferChipModal
        open={addOwnerModal.open}
        onClose={addOwnerModal.close}
        chip={addOwnerModal.data}
        device={addOwnerDevice}
        onTransfer={handleAddOwnerSubmit}
      />
    </div>
  );
}
